import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper 
} from '@mui/material';
import { 
  CheckCircleOutline, 
  CloudSync, 
  Storefront, 
  Kitchen, 
  TwoWheeler, 
  PieChart 
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import PublicLayout from "../../layouts/PublicLayout";

export default function CloudKitchen() {
  return (
    <PublicLayout>
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="flex-start">
            
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: '#f6521a', fontWeight: 800, letterSpacing: 1.5 }}>
                TPOS FOR CLOUD KITCHENS & DELIVERY HUBS
              </Typography>
              
              <Typography variant="h2" sx={{ fontWeight: 600, mt: 1, mb: 2, fontSize: { xs: '1.5rem', md: '2.5rem' }, lineHeight: 1.1 }}>
                Master the Delivery Game with a <span style={{ color: "#f6521a" }}>Unified Kitchen Dashboard.</span>
              </Typography>

              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.5 }}>
                Manage Swiggy, Zomato, your direct website, and multiple virtual brands from a single, chaotic-free TPOS screen.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.primary', mb: 5, fontSize: '1.1rem', lineHeight: 1.8 }}>
                Cloud kitchens run on a different frequency than traditional restaurants. Without a physical storefront, your efficiency, prep time, and digital presence are everything. 
                <strong> Juggling four different aggregator tablets while managing orders for three virtual brands is a recipe for disaster. </strong> 
                TPOS acts as the central nervous system of your dark kitchen, pulling all orders into one unified workflow while maintaining strict inventory control.
              </Typography>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Specialized TPOS Features:
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    title: "Native Aggregator Integrations",
                    desc: "Seamless API connections with Zomato, Swiggy, and direct ordering websites. All orders land directly on your POS with sound alerts.",
                    icon: <CloudSync sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Multi-Brand Menu Management", 
                    desc: "Run a burger, pizza, and biryani brand from the same kitchen. Maintain separate digital menus mapped to shared raw materials.",
                    icon: <Storefront sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Centralized KDS", 
                    desc: "Replace paper KOTs with smart screens. Color-coded tracking ensures chefs know exactly what to prep first based on rider arrival.",
                    icon: <Kitchen sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Rider Dispatch Logic", 
                    desc: "Track order statuses from 'Accepted' to 'Dispatched.' Send automated SMS updates to direct-order customers.",
                    icon: <TwoWheeler sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Costing & Yield Management", 
                    desc: "Know the exact profit margin on every dish delivered, factoring in packaging costs and aggregator commissions.",
                    icon: <PieChart sx={{ color: '#f6521a' }} />
                  }
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ mt: 0.5 }}>{item.icon}</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Grid>

            {/* RIGHT IMAGE */}
            <Grid item xs={12} md={5} sx={{ position: 'sticky', top: 100 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden', 
                  background: 'linear-gradient(145deg, #ffffff 0%, #f9f9f9 100%)',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 550 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'right',
                    float:'right'                    
                  }}
                >
                  <img 
                    src="/img/home-banner.jpg" 
                    alt="Cloud Kitchen POS Management" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
                
                <Box sx={{ p: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Business Impact</Typography>
                  <List dense>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Eliminate 100% of manual order entry errors from third-party apps." />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Lower order cancellation rates by preventing stockouts through real-time sync." />
                    </ListItem>
                  </List>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </PublicLayout>
  );
}